import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BarberSignupModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    city: '',
    experience_years: 0,
    specialties: [],
    has_tools: false,
    has_license: false
  });

  const cities = ['Lagos', 'Abuja', 'Ibadan', 'Port Harcourt', 'Kano', 'Enugu', 'Benin City'];
  const specialtyOptions = [
    'Haircuts',
    'Beard Grooming',
    'Hair Styling',
    'Kids Cuts',
    'Fade Specialist',
    'Traditional Barber'
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecialtyToggle = (specialty) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.includes(specialty)
        ? prev.specialties.filter(s => s !== specialty)
        : [...prev.specialties, specialty]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/barbers/signup`, formData);
      
      // Store barber data and redirect to dashboard
      localStorage.setItem('barbergo_barber', JSON.stringify(response.data));
      
      toast.success('Welcome to BarberGo! Redirecting to your dashboard...');
      onClose();
      
      // Redirect to barber dashboard
      setTimeout(() => {
        navigate('/barber-dashboard');
      }, 1000);
      
      // Reset form
      setFormData({
        full_name: '',
        phone: '',
        email: '',
        city: '',
        experience_years: 0,
        specialties: [],
        has_tools: false,
        has_license: false
      });
    } catch (error) {
      console.error('Error submitting barber signup:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div data-testid="barber-signup-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border border-[var(--border-color)] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border-b border-[var(--border-color)] p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[var(--text-primary)]" data-testid="barber-modal-title">Become a Barber</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Join our team and start earning</p>
          </div>
          <button
            onClick={onClose}
            data-testid="close-barber-modal"
            className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Details */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="full_name" className="text-[var(--text-primary)]">Full Name *</Label>
              <Input
                id="full_name"
                data-testid="barber-name-input"
                required
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone" className="text-[var(--text-primary)]">Phone Number *</Label>
                <Input
                  id="phone"
                  data-testid="barber-phone-input"
                  required
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-[var(--text-primary)]">Email *</Label>
                <Input
                  id="email"
                  data-testid="barber-email-input"
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="text-[var(--text-primary)]">City *</Label>
                <Select value={formData.city} onValueChange={(value) => handleChange('city', value)}>
                  <SelectTrigger data-testid="barber-city-select" className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)]">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-secondary)] dark:bg-[#1A1A1A] border-[var(--border-color)] text-[var(--text-primary)]">
                    {cities.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="experience_years" className="text-[var(--text-primary)]">Years of Experience *</Label>
                <Input
                  id="experience_years"
                  data-testid="barber-experience-input"
                  required
                  type="number"
                  min="0"
                  value={formData.experience_years}
                  onChange={(e) => handleChange('experience_years', parseInt(e.target.value) || 0)}
                  className="mt-1 bg-[var(--bg-primary)] dark:bg-[#111111] border-[var(--border-color)] text-[var(--text-primary)] focus:border-[#00C167] focus:ring-[#00C167]/20"
                />
              </div>
            </div>
          </div>

          {/* Specialties */}
          <div>
            <Label className="text-[var(--text-primary)] mb-3 block">Specialties * (Select all that apply)</Label>
            <div className="grid grid-cols-2 gap-3" data-testid="barber-specialties">
              {specialtyOptions.map(specialty => (
                <div key={specialty} className="flex items-center space-x-2">
                  <Checkbox
                    id={specialty}
                    data-testid={`specialty-${specialty.toLowerCase().replace(/\s+/g, '-')}`}
                    checked={formData.specialties.includes(specialty)}
                    onCheckedChange={() => handleSpecialtyToggle(specialty)}
                    className="border-[var(--border-color)] data-[state=checked]:bg-[#00C167] data-[state=checked]:border-[#00C167]"
                  />
                  <label
                    htmlFor={specialty}
                    className="text-sm text-[var(--text-primary)] cursor-pointer"
                  >
                    {specialty}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Requirements */}
          <div className="space-y-3">
            <Label className="text-[var(--text-primary)]">Requirements</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="has_tools"
                data-testid="barber-has-tools"
                checked={formData.has_tools}
                onCheckedChange={(checked) => handleChange('has_tools', checked)}
                className="border-[var(--border-color)] data-[state=checked]:bg-[#00C167] data-[state=checked]:border-[#00C167]"
              />
              <label htmlFor="has_tools" className="text-sm text-[var(--text-primary)] cursor-pointer">
                I have my own professional tools
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="has_license"
                data-testid="barber-has-license"
                checked={formData.has_license}
                onCheckedChange={(checked) => handleChange('has_license', checked)}
                className="border-[var(--border-color)] data-[state=checked]:bg-[#00C167] data-[state=checked]:border-[#00C167]"
              />
              <label htmlFor="has_license" className="text-sm text-[var(--text-primary)] cursor-pointer">
                I have a valid barber license/certification
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-barber-signup"
              className="flex-1 border-[var(--border-color)] text-[var(--text-primary)] hover:bg-[var(--bg-primary)]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || formData.specialties.length === 0}
              data-testid="submit-barber-signup"
              className="flex-1 bg-[#00C167] hover:bg-[#009E54] text-white"
            >
              {loading ? 'Submitting...' : 'Submit Application'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BarberSignupModal;
